import { Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { useCategories, useDocs } from '../hooks/useDocs';
import Sidebar from '../components/modules/Sidebar';
import DocCard from '../components/modules/DocCard';
import DocReader from '../components/modules/DocReader';
import SectionHeader from '../components/modules/SectionHeader';
import TagFilter from '../components/modules/TagFilter';
import SearchBar from '../components/modules/SearchBar';
import CardGrid from '../components/modules/CardGrid';
import { useAppStore } from '../store/useAppStore';
import styles from './Gallery.module.css';

function CategoryView() {
  const { category } = useParams<{ category: string }>();
  const { categories } = useCategories();
  const { docs: allDocs, loading } = useDocs(category, 'published');

  const cat = categories.find((c) => c.slug === category);

  const { searchQuery: sq, setSearchQuery, activeTags: at, toggleTag: tt, clearAll } = useAppStore();

  // Collect all unique tags from docs
  const allTags = Array.from(
    new Set(allDocs.flatMap((d) => d.tags))
  ).sort();

  // Filter docs
  let filtered = [...allDocs];

  if (sq.trim()) {
    const q = sq.toLowerCase();
    filtered = filtered.filter(
      (d) =>
        d.title.toLowerCase().includes(q) ||
        d.summary.toLowerCase().includes(q) ||
        d.tags.some((t) => t.toLowerCase().includes(q))
    );
  }

  if (at.length > 0) {
    filtered = filtered.filter((d) => at.some((tag) => d.tags.includes(tag)));
  }

  if (loading) {
    return (
      <div className={styles.categoryView}>
        <div className={styles.empty}>LOADING...</div>
      </div>
    );
  }

  if (!cat) {
    return <div className={styles.notFound}>Category not found</div>;
  }

  return (
    <div className={styles.categoryView}>
      <SectionHeader title={cat.title} subtitle={cat.description} icon={cat.icon} />

      <SearchBar
        value={sq}
        onChange={setSearchQuery}
        placeholder="SEARCH ARCHIVES..."
      />

      <TagFilter
        allTags={allTags}
        activeTags={at}
        onToggleTag={tt}
        onClearAll={clearAll}
        filteredCount={filtered.length}
        totalCount={allDocs.length}
      />

      <CardGrid
        cards={
          filtered.length > 0
            ? filtered.map((doc) => <DocCard key={doc.slug} doc={doc} category={category!} />)
            : [<div key="empty" className={styles.empty}>NO ARCHIVES MATCH CURRENT FILTER</div>]
        }
      />
    </div>
  );
}

function DocView() {
  return (
    <div className={styles.docView}>
      <DocReader />
    </div>
  );
}

export default function Gallery() {
  return (
    <div className={styles.gallery}>
      <Sidebar />
      <div className={styles.content}>
        <Routes>
          <Route path="/" element={<CategoryIndex />} />
          {/* 静态段 "all" 优先于动态 :category 匹配 */}
          <Route path="all" element={<AllDocsView />} />
          <Route path=":category" element={<CategoryView />} />
          <Route path=":category/:slug" element={<DocView />} />
        </Routes>
      </div>
    </div>
  );
}

/** 分类入口页：展示所有分类卡片 */
function CategoryIndex() {
  const navigate = useNavigate();
  const { categories, loading } = useCategories();
  const sortedCategories = [...categories].sort((a, b) => a.slug.localeCompare(b.slug));

  if (loading) {
    return (
      <div className={styles.indexView}>
        <div className={styles.empty}>LOADING...</div>
      </div>
    );
  }

  return (
    <div className={styles.indexView}>
      <SectionHeader
        title="ARCHIVE INDEX"
        subtitle="Select a category to browse documents"
        icon="◈"
      />

      <div className={styles.categoryGrid}>
        {sortedCategories.map((cat) => (
          <button
            key={cat.slug}
            className={styles.categoryCard}
            onClick={() => navigate(`/archive/${cat.slug}`)}
          >
            <span className={styles.catIcon}>{cat.icon}</span>
            <span className={styles.catTitle}>{cat.title}</span>
            <span className={styles.catDesc}>{cat.description}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** 全部文档页：跨分类的全量文档列表（搜索 + 标签过滤） */
function AllDocsView() {
  const { docs: allDocs, loading } = useDocs(undefined, 'published');

  const { searchQuery: sq, setSearchQuery, activeTags: at, toggleTag: tt, clearAll } = useAppStore();

  // Collect all unique tags from docs
  const allTags = Array.from(
    new Set(allDocs.flatMap((d) => d.tags))
  ).sort();

  // Filter docs
  let filtered = [...allDocs];

  if (sq.trim()) {
    const q = sq.toLowerCase();
    filtered = filtered.filter(
      (d) =>
        d.title.toLowerCase().includes(q) ||
        d.summary.toLowerCase().includes(q) ||
        d.tags.some((t) => t.toLowerCase().includes(q))
    );
  }

  if (at.length > 0) {
    filtered = filtered.filter((d) => at.some((tag) => d.tags.includes(tag)));
  }

  if (loading) {
    return (
      <div className={styles.indexView}>
        <div className={styles.empty}>LOADING...</div>
      </div>
    );
  }

  return (
    <div className={styles.indexView}>
      <SectionHeader
        title="ARCHIVE INDEX"
        subtitle="All documents across categories"
        icon="◈"
      />

      <SearchBar
        value={sq}
        onChange={setSearchQuery}
        placeholder="SEARCH ARCHIVES..."
      />

      <TagFilter
        allTags={allTags}
        activeTags={at}
        onToggleTag={tt}
        onClearAll={clearAll}
        filteredCount={filtered.length}
        totalCount={allDocs.length}
      />

      <CardGrid
        cards={
          filtered.length > 0
            ? filtered.map((doc) => <DocCard key={doc.slug} doc={doc} category={doc.category} />)
            : [<div key="empty" className={styles.empty}>NO ARCHIVES MATCH CURRENT FILTER</div>]
        }
      />
    </div>
  );
}
